/**
 * AgentPerformance.jsx — per-agent ticket handling report.
 *
 * Pulls /analytics/agent_performance (admin proxy → chat_service, enriched with
 * agent names) and shows, per agent: tickets assigned, closed, "not replied and
 * closed", auto-timeout closes, avg resolution time, resolution rate and CSAT.
 * Org-scoped by the backend, so an admin only sees their own organisation's agents.
 */
import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Card, Form, Spinner } from "react-bootstrap";
import APICall from "../../../APICalls/APICall";

const RANGES = [
  { v: 7,  l: "Last 7 days" },
  { v: 30, l: "Last 30 days" },
  { v: 90, l: "Last 90 days" },
];

const fmtMins = (m) => {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${Math.round(m)}m`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m % 60);
  return rem ? `${h}h ${rem}m` : `${h}h`;
};

const AgentPerformance = () => {
  const [rows, setRows]     = useState([]);
  const [range, setRange]   = useState(30);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState("");

  const load = useCallback(async (r) => {
    setLoad(true);
    setError("");
    try {
      const res = await APICall.getT(`/analytics/agent_performance?range=${r}`);
      setRows(Array.isArray(res) ? res : []);
    } catch {
      setError("Agent performance unavailable — ensure the analytics service is running.");
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => { load(range); }, [load, range]);

  return (
    <Card className="rounded-4 shadow-sm mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h5 className="mb-0">Agent Performance</h5>
            <small className="text-muted">Tickets assigned, closed & unresolved per agent</small>
          </div>
          <Form.Select
            size="sm"
            style={{ width: 160 }}
            value={range}
            onChange={(e) => setRange(Number(e.target.value))}
          >
            {RANGES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </Form.Select>
        </div>

        {loading ? (
          <div className="text-center py-4"><Spinner size="sm" /></div>
        ) : error ? (
          <Alert variant="warning" className="py-2">{error}</Alert>
        ) : rows.length === 0 ? (
          <div className="text-center text-muted py-4">No agent activity in this period.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 60 }}>S.No</th>
                  <th>Agent</th>
                  <th className="text-center">Assigned</th>
                  <th className="text-center">Open</th>
                  <th className="text-center">Closed</th>
                  <th className="text-center" title="Closed without the agent ever replying to the customer">
                    Not Replied&nbsp;&amp; Closed
                  </th>
                  <th className="text-center" title="Auto-closed because the agent stayed silent">Auto-Timeout</th>
                  <th className="text-center">Avg Resolution</th>
                  <th className="text-center">Resolution Rate</th>
                  <th className="text-center">CSAT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a, i) => (
                  <tr key={a.agent_id}>
                    <td className="text-muted">{i + 1}</td>
                    <td className="fw-semibold">{a.agent_name || `Agent #${a.agent_id}`}</td>
                    <td className="text-center">{a.assigned_tickets}</td>
                    <td className="text-center">
                      {a.open_tickets > 0 ? <Badge bg="warning" text="dark">{a.open_tickets}</Badge> : 0}
                    </td>
                    <td className="text-center">{a.closed_tickets}</td>
                    <td className="text-center">
                      {a.closed_without_reply > 0
                        ? <Badge bg="danger">{a.closed_without_reply}</Badge>
                        : <span className="text-muted">0</span>}
                    </td>
                    <td className="text-center">
                      {a.auto_closed_timeout > 0
                        ? <Badge bg="secondary">{a.auto_closed_timeout}</Badge>
                        : <span className="text-muted">0</span>}
                    </td>
                    <td className="text-center">{fmtMins(a.avg_resolution_minutes)}</td>
                    <td className="text-center">
                      <Badge bg={a.resolution_rate >= 70 ? "success" : a.resolution_rate >= 40 ? "warning" : "danger"}
                             text={a.resolution_rate >= 40 && a.resolution_rate < 70 ? "dark" : undefined}>
                        {a.resolution_rate}%
                      </Badge>
                    </td>
                    <td className="text-center">{a.avg_csat != null ? `${a.avg_csat}★` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AgentPerformance;
